import { serialize } from './query-string';

type QsValue =
  | string
  | number
  | boolean
  | Date
  | Array<string | number | boolean | Date | object>
  | object;

type Url<T extends string> = T extends `${infer _Start}?${infer _Rest}`
  ? never
  : T;

type DownloadBlobOptions<T extends string> = {
  url: Url<T>;
  token?: string;
  filename?: string;
  method?: 'GET' | 'POST';
  body?: BodyInit | null;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** If true, opens the file in a new tab for preview instead of downloading. Defaults to false. */
  inline?: boolean;
  qs?: Record<string, QsValue>;
};

export const download = async <T extends string>({
  url,
  token,
  method = 'GET',
  body = null,
  headers = {},
  signal,
  qs = {},
  inline = false,
}: DownloadBlobOptions<T>) => {
  let blobUrl = '';
  let ok = false;
  document.body.style.cursor = 'wait';
  try {
    const queryString = serialize({
      ...qs,
      ...(inline ? { disposition: 'inline' } : {}),
    });
    const finalUrl = queryString ? `${url}?${queryString}` : url;
    const response = await fetch(finalUrl, {
      method,
      body,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      signal,
    });

    if (!response.ok) {
      throw new Error(`Unable to download file, (${response.status})`);
    }
    const blob = await response.blob();
    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    blobUrl = window.URL.createObjectURL(blob);

    let filename = 'download'; // fallback filename

    if (contentDisposition) {
      // Try to extract filename from filename*=UTF-8''... format first
      const filenameStarMatch = contentDisposition.match(
        /filename\*=UTF-8''([^;]+)/,
      );
      if (filenameStarMatch) {
        filename = decodeURIComponent(filenameStarMatch[1]);
      } else {
        const filenameQuotedMatch =
          contentDisposition.match(/filename="([^"]+)"/);
        if (filenameQuotedMatch) {
          filename = decodeURIComponent(filenameQuotedMatch[1]);
        } else {
          const filenameMatch = contentDisposition.match(/filename=([^;\s]+)/);
          if (filenameMatch) {
            filename = decodeURIComponent(filenameMatch[1].trim());
          }
        }
      }
    }

    if (inline) {
      window.open(blobUrl, '_blank');
    } else {
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    ok = true;
  } catch (error) {
    ok = false;
  } finally {
    if (blobUrl !== '') {
      if (!inline) {
        window.URL.revokeObjectURL(blobUrl);
      }
    }
    document.body.style.cursor = 'default';
  }
  return ok;
};

const blobToDataURL = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

export type GetDataUrlOptions<T extends string> = Omit<
  DownloadBlobOptions<T>,
  'filename'
>;

export const getDataUrl = async <T extends string>({
  url,
  token,
  method = 'GET',
  body = null,
  headers = {},
  signal,
}: GetDataUrlOptions<T>): Promise<string | null> => {
  try {
    const res = await fetch(url, {
      method,
      body,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      signal,
    });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await blobToDataURL(blob);
  } catch {
    return null;
  }
};
